import {
  IconArrowProfile,
  IconContact,
  IconInstagram,
  IconMap,
  IconMessage,
  IconPhone,
  IconRatingStar,
  IconTelegram,
} from '@app/icons';
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { setMask } from '@app/helpers';
import {
  User,
  UserPhone,
  UserStatus,
  UserStatusNames,
  UserType,
} from '@app/models';
import { useTranslation } from 'react-i18next';
import { updateUser } from '@app/api';
import { useUserContext } from '@app/providers';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Badge } from 'components/ui/badge';
import { Card } from 'components/ui/card';

interface IAboutUser {
  id?: string;
  ratingUser?: number;
  views: number;
  lastDateUpdate: number;
  email: string | null;
  instagram: string | null;
  telegram: string | null;
  phones: string | null;
  status?: UserStatus | null;
  webSite?: string | null;
  whatAppNumber?: string | null;
  phone?: string | null;
  editable?: boolean;
  userType?: UserType;
}

interface IUserStatus {
  key: UserStatus;
  value: string;
}

const AboutUser: React.FC<IAboutUser> = memo(
  ({
    id,
    ratingUser = 0,
    views = 0,
    lastDateUpdate = 0,
    email = '',
    instagram = '',
    telegram = '',
    phones = '',
    status,
    webSite = '',
    whatAppNumber,
    phone,
    editable = false,
    userType,
  }) => {
    const statusList: IUserStatus[] = [
      {
        key: UserStatus.FREE,
        value: UserStatusNames[UserStatus.FREE],
      },
      {
        key: UserStatus.BUSY,
        value: UserStatusNames[UserStatus.BUSY],
      },
    ];
    const { user } = useUserContext();
    const isCurrentUserProfile = user.id === id;
    const { t } = useTranslation();
    const [ratingArr] = useState([1, 2, 3, 4, 5]);
    const [rating, setRating] = useState(ratingUser);
    const [selectedStatus, setSelectedStatus] = useState(
      status ? UserStatusNames[status] : UserStatusNames[UserStatus.FREE]
    );

    // Показываем рейтинг только для компаний (ENTITY), но не для специалистов (INDIVIDUAL)
    const showRating = userType !== UserType.INDIVIDUAL;

    const parsedPhones = useMemo(() => {
      if (!phones || phones.length === 0) return [];
      try {
        return JSON.parse(phones);
      } catch {
        return [];
      }
    }, [phones]);

    const mainPhone = useMemo(() => {
      if (phone) return setMask(phone, '+7 (###) ### ## ##');
      if (parsedPhones.length > 0)
        return setMask(parsedPhones[0].phone, '+7 (###) ### ## ##');
      return null;
    }, [phone, parsedPhones]);

    const preferPhone = useMemo(() => {
      if (!parsedPhones.length) return null;
      const found = parsedPhones.find((p: UserPhone) => p.isPrefer);
      if (!found) return null;
      const masked = setMask(found.phone, '+7 (###) ### ## ##');
      if (masked === mainPhone) return null;
      return masked;
    }, [parsedPhones, mainPhone]);

    const whatsAppPhone = useMemo(() => {
      if (!parsedPhones.length) return null;
      const found = parsedPhones.find((p: UserPhone) => p.isWhatsApp);
      if (!found) return null;
      const masked = setMask(found.phone, '+7 (###) ### ## ##');
      if (masked === mainPhone) return null;
      return masked;
    }, [parsedPhones, mainPhone]);

    const otherPhones = useMemo(() => {
      if (!parsedPhones.length) return [];
      return parsedPhones
        .filter(
          (p: UserPhone) =>
            !p.isPrefer &&
            !p.isWhatsApp &&
            setMask(p.phone, '+7 (###) ### ## ##') !== mainPhone
        )
        .map((p: UserPhone) => setMask(p.phone, '+7 (###) ### ## ##'));
    }, [parsedPhones, mainPhone]);

    const onSelectStatus = useCallback(
      async (selectedUser: IUserStatus) => {
        try {
          if (!editable || !id) {
            return;
          }
          setSelectedStatus(selectedUser.value);
          await updateUser(id, { status: selectedUser.key } as User);
        } catch (e) {}
      },
      [editable, id]
    );

    return (
      <Card className="mt-4 p-4 w-full">
        {isCurrentUserProfile && (
          <div className="mb-4">
            <Badge variant="secondary">Виден в поиске</Badge>
          </div>
        )}

        {user.visible_on_search &&
          user.type === UserType.INDIVIDUAL &&
          !!status && (
            <div className="mb-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={!editable}>
                  <Button
                    variant={
                      selectedStatus === UserStatusNames[UserStatus.BUSY]
                        ? 'destructive'
                        : 'default'
                    }
                    size="sm"
                    className="w-24 justify-between"
                  >
                    {selectedStatus}
                    {editable && <IconArrowProfile className="ml-1 h-3 w-3" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-24">
                  {statusList.map((status) => (
                    <DropdownMenuItem
                      key={status.key}
                      onClick={() => onSelectStatus(status)}
                      className={cn(
                        status.key === UserStatus.BUSY
                          ? 'text-destructive focus:text-destructive'
                          : 'text-success focus:text-success'
                      )}
                    >
                      {status.value}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

        {showRating && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-muted-foreground">
              Рейтинг: {rating || 0}/5
            </span>
            <div className="flex items-center gap-1">
              {ratingArr.map((star, index) => (
                <IconRatingStar
                  key={index}
                  className={cn(
                    'h-4 w-4',
                    index < rating && 'text-blue-500 fill-blue-500'
                  )}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 mb-4 text-sm text-muted-foreground">
          <IconContact className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            Контактные сведения обновлены менее {lastDateUpdate.toString()} мес.
            назад
          </span>
        </div>

        <div className="space-y-3">
          {(mainPhone ||
            preferPhone ||
            whatsAppPhone ||
            otherPhones.length > 0) && (
            <div className="flex items-start gap-2">
              <IconPhone className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {mainPhone && (
                  <div className="flex flex-wrap items-baseline gap-1">
                    <span className="font-mono">{mainPhone}</span>
                    <span className="text-xs text-muted-foreground">
                      - основной номер
                    </span>
                  </div>
                )}
                {preferPhone && (
                  <div className="flex flex-wrap items-baseline gap-1">
                    <span className="font-mono">{preferPhone}</span>
                    <span className="text-xs text-muted-foreground">
                      - предпочитаемый способ связи
                    </span>
                  </div>
                )}
                {whatsAppPhone && (
                  <div className="flex flex-wrap items-baseline gap-1">
                    <span className="font-mono">{whatsAppPhone}</span>
                    <span className="text-xs text-muted-foreground">
                      - писать на WhatsApp
                    </span>
                  </div>
                )}
                {otherPhones.map((p: string, i: number) => (
                  <div key={i} className="font-mono">
                    {p}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm">
            <IconMessage className="h-4 w-4 flex-shrink-0" />
            <span className={cn(!email && 'text-muted-foreground')}>
              {email || t('notSpecified')}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <IconInstagram className="h-4 w-4 flex-shrink-0" />
            <span className={cn(!instagram && 'text-muted-foreground')}>
              {instagram || t('notSpecified')}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <IconTelegram className="h-4 w-4 flex-shrink-0" />
            <span className={cn(!telegram && 'text-muted-foreground')}>
              {telegram || t('notSpecified')}
            </span>
          </div>

          {webSite && (
            <div className="flex items-center gap-2 text-sm">
              <IconMap className="h-4 w-4 flex-shrink-0" />
              <span>{webSite}</span>
            </div>
          )}
        </div>
      </Card>
    );
  }
);

export default AboutUser;
